/**
 * Configuration centralisée des workers
 * Source unique de vérité pour les URLs de tous les workers
 * 
 * Utilisé par :
 * - Frontend (pages/wa-bellow)
 * - Workers (wab-chatbot, etc.)
 * 
 * Détecte automatiquement dev/prod selon l'environnement
 */

/**
 * Configuration des URLs des workers par environnement
 * @type {Object}
 */
export const WORKERS_CONFIG = {
  development: {
    CONFIG: 'http://localhost:63270',
    WA_PROXY: 'http://localhost:64033',
    IA_PROXY: 'http://localhost:65432',
    CHATBOT: 'http://localhost:65433'
  },
  production: {
    CONFIG: 'https://wab-config-production.bip-philippe.workers.dev',
    WA_PROXY: 'https://wab-waproxy-production.bip-philippe.workers.dev',
    IA_PROXY: 'https://wab-iaproxy-production.bip-philippe.workers.dev',
    CHATBOT: 'https://wab-chatbot-production.bip-philippe.workers.dev'
  }
};

/**
 * Détecte l'environnement courant
 * Frontend: utilise import.meta.env (Vite)
 * Workers: utilise process.env ou paramètre explicite
 * @param {string} [env='development'] - Environnement explicite si nécessaire
 * @returns {string} 'development' ou 'production'
 */
export function detectEnvironment(env = null) {
  // Cas 1 : Paramètre explicite (utile pour les workers)
  if (env && (env === 'development' || env === 'production')) {
    return env;
  }

  // Cas 2 : Variable d'environnement Node.js (workers)
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.ENVIRONMENT === 'production') return 'production';
    if (process.env.NODE_ENV === 'production') return 'production';
  }

  // Cas 3 : Vite (frontend)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    if (import.meta.env.VITE_NODE_ENV === 'production') return 'production';
    if (import.meta.env.PROD) return 'production';
  }

  // Défaut : development
  return 'development';
}

/**
 * Récupère la configuration des workers pour l'environnement courant
 * @param {string} [env='auto'] - Environnement ('development', 'production', ou 'auto' pour détection)
 * @returns {Object} Configuration avec toutes les URLs des workers
 */
export function getWorkersConfig(env = 'auto') {
  const environment = env === 'auto' ? detectEnvironment() : env;
  return WORKERS_CONFIG[environment] || WORKERS_CONFIG.development;
}

/**
 * Récupère une URL spécifique d'un worker
 * @param {string} workerName - Nom du worker ('CONFIG', 'WA_PROXY', 'IA_PROXY', 'CHATBOT')
 * @param {string} [env='auto'] - Environnement (auto/development/production)
 * @returns {string} URL du worker
 */
export function getWorkerUrl(workerName, env = 'auto') {
  const config = getWorkersConfig(env);
  const url = config[workerName];
  
  if (!url) {
    throw new Error(
      `Worker "${workerName}" non trouvé. Disponibles: ${Object.keys(config).join(', ')}`
    );
  }
  
  return url;
}

/**
 * Valide que toutes les URLs sont accessibles (utility pour debug)
 * @param {string} [env='auto'] - Environnement à valider
 * @returns {Object} Résumé de la validation
 */
export async function validateWorkersConfig(env = 'auto') {
  const config = getWorkersConfig(env);
  const results = {};

  for (const [name, url] of Object.entries(config)) {
    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
      results[name] = { url, status: response.status, ok: true };
    } catch (error) {
      results[name] = { url, error: error.message, ok: false };
    }
  }

  return results;
}

/**
 * Export par défaut de la configuration
 * Utilisation simple : import { getWorkersConfig } from '@wa-bellow/commons-shared/workers-config'
 */
export default {
  WORKERS_CONFIG,
  detectEnvironment,
  getWorkersConfig,
  getWorkerUrl,
  validateWorkersConfig
};
